/**
 * سيارتي 360 — Axios-based API client
 * Handles JWT auth, token refresh, and base URL configuration.
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const RAW_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
const LOCAL_API_PORT = '8000';

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function normalizeLocalApiBaseUrl(baseUrl: string): string {
  try {
    const parsed = new URL(baseUrl);
    if (!isLocalHost(parsed.hostname)) return baseUrl;
    if (parsed.port === LOCAL_API_PORT) return baseUrl;
    // In local development we standardize backend API to :8000 to avoid
    // stale env values (e.g. :8001) causing noisy connection failures.
    parsed.port = LOCAL_API_PORT;
    return parsed.origin;
  } catch {
    return baseUrl;
  }
}

const BASE_URL = normalizeLocalApiBaseUrl(RAW_BASE_URL);

// Token storage keys
const ACCESS_TOKEN_KEY = 's360_access';
const REFRESH_TOKEN_KEY = 's360_refresh';
const COUNTRY_CODE_KEY = 'app_country_code';

export const tokenStore = {
  getAccess: () => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  getRefresh: () => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  set: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

const apiClient: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

type ApiRequestConfig = AxiosRequestConfig & {
  skipAuth?: boolean;
  _localBaseRetried?: boolean;
  _runtimeHostFallbackRetried?: boolean;
  _resolvedBaseBeforeRuntimeSwap?: string | null;
  _retry?: boolean;
};

function resolveRuntimeApiBase(baseURL?: string | null): string | null {
  if (typeof window === 'undefined' || !baseURL) return null;
  try {
    const parsed = new URL(baseURL);
    const runtimeHost = window.location.hostname;
    // If configured API host is localhost but app is opened over LAN IP/domain,
    // use the same runtime host to keep client-side API calls reachable.
    if (!isLocalHost(parsed.hostname) || isLocalHost(runtimeHost)) return null;
    parsed.hostname = runtimeHost;
    parsed.port = LOCAL_API_PORT;
    return `${parsed.origin}/api`;
  } catch {
    return null;
  }
}

function getLocalFallbackBase(baseURL?: string | null): string | null {
  if (!baseURL) return null;
  try {
    const parsed = new URL(baseURL);
    if (!isLocalHost(parsed.hostname)) return null;
    if (parsed.port === LOCAL_API_PORT) return null;
    return `${parsed.protocol}//${parsed.hostname}:${LOCAL_API_PORT}`;
  } catch {
    return null;
  }
}

function getStoredCountryCode(): string {
  if (typeof window === 'undefined') return '';
  return (localStorage.getItem(COUNTRY_CODE_KEY) || '').trim().toUpperCase();
}

// ── Request interceptor: attach access token ──────────────────────────────
apiClient.interceptors.request.use((config) => {
  const requestConfig = config as ApiRequestConfig;
  const currentBase = requestConfig.baseURL || apiClient.defaults.baseURL;
  requestConfig._resolvedBaseBeforeRuntimeSwap = currentBase || null;
  const runtimeBase = resolveRuntimeApiBase(currentBase);
  if (runtimeBase) {
    requestConfig.baseURL = runtimeBase;
  }

  if (requestConfig.skipAuth) {
    if (requestConfig.headers && 'Authorization' in requestConfig.headers) {
      delete requestConfig.headers.Authorization;
    }
    return config;
  }

  const token = tokenStore.getAccess();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const countryCode = getStoredCountryCode();
  if (countryCode && config.headers) {
    config.headers['X-Country-Code'] = countryCode;
  }
  return config;
});

// ── Response interceptor: refresh JWT on 401 ─────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token!);
  });
  failedQueue = [];
};

// ── ApiResponse Interface ────────────────────────────────────────────────
export interface ApiResponse<T = any> {
  status: 'success' | 'error';
  message: string;
  data: T;
  errors: any | null;
}

apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // If it's a standard API response, unwrap the 'data'
    if (response.data && response.data.status) {
      return response.data as any;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config as ApiRequestConfig;
    const ensureHeaders = () => {
      if (!originalRequest.headers) {
        originalRequest.headers = {} as any;
      }
      return originalRequest.headers as any;
    };

    // If runtime host auto-swap fails (e.g. backend bound to localhost only),
    // retry once using the original configured API host.
    if (
      !error.response &&
      originalRequest &&
      !originalRequest._runtimeHostFallbackRetried &&
      originalRequest._resolvedBaseBeforeRuntimeSwap &&
      originalRequest.baseURL !== originalRequest._resolvedBaseBeforeRuntimeSwap
    ) {
      originalRequest._runtimeHostFallbackRetried = true;
      originalRequest.baseURL = originalRequest._resolvedBaseBeforeRuntimeSwap;
      return apiClient(originalRequest);
    }

    // Handle local dev misconfiguration gracefully: if configured local API port is wrong,
    // retry once against :8000 before failing.
    const fallbackBase = getLocalFallbackBase(originalRequest?.baseURL || apiClient.defaults.baseURL);
    if (!error.response && originalRequest && !originalRequest._localBaseRetried && fallbackBase) {
      originalRequest._localBaseRetried = true;
      originalRequest.baseURL = `${fallbackBase}/api`;
      return apiClient(originalRequest);
    }

    // ... (rest of the refresh logic)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = tokenStore.getRefresh();
      if (!refreshToken) {
        tokenStore.clear();
        if (typeof window !== 'undefined') window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            ensureHeaders().Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${BASE_URL}/api/auth/token/refresh/`, {
          refresh: refreshToken,
        });
        const newAccess: string = data.access;
        tokenStore.set(newAccess, refreshToken);
        apiClient.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
        processQueue(null, newAccess);
        ensureHeaders().Authorization = `Bearer ${newAccess}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        tokenStore.clear();
        if (typeof window !== 'undefined') window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Standardize error format for UI consumption
    const apiError = error.response?.data as ApiResponse;
    if (apiError && apiError.status === 'error') {
      return Promise.reject(apiError);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
